import { CommonModule } from "@angular/common";
import { StrategyGroupEx } from "@front/m19-models";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import { IButtonDirective, IIconComponent, MODAL_DATA, ModalRef, ModalService, ToasterService } from "@front/m19-ui";
import { Component, computed, inject, signal, TemplateRef, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountMarketplace, Strategy, StrategyType } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { SpStrategyEx } from "@front/m19-models/strategy/SpStrategyEx";
import { AccountSelectionService, AsinService, SpStrategyService, TacosStrategiesService } from "@front/m19-services";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  ConfirmProductsDeletionModalComponent,
  ConfirmProductsDeletionModalData,
} from "@m19-board/sponsored-product/sp-page/strategy-group/confirm-products-deletion-modal/confirm-products-deletion-modal.component";
import { forkJoin, map, switchMap, take, tap } from "rxjs";
import {
  StrategyGroupMoveProductModalComponent,
  StrategyGroupMoveProductModalData,
  TargetProductStrategy,
} from "../../move-product/strategy-group-move-product-modal.component";

export interface StrategyGroupProductsModalData {
  strategyGroup: StrategyGroupEx;
  strategy: SpStrategyEx;
  isReadOnly: boolean;
}

@Component({
  selector: "app-strategy-products-modal",
  imports: [CommonModule, AsinSelectionComponent, IButtonDirective, TranslocoDirective, IIconComponent],
  templateUrl: "./strategy-products-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyProductsModalComponent {
  private readonly asinService = inject(AsinService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly strategyService = inject(StrategyService);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly toastService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);

  @ViewChild("addProductModal") addProductModal!: TemplateRef<any>;
  addProductModalRef: ModalRef<any> | undefined;

  modalData = inject<StrategyGroupProductsModalData>(MODAL_DATA);
  modalRef = inject(ModalRef);

  strategy$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am: AccountMarketplace) => this.spStrategyService.getStrategies(am.accountId, am.marketplace)),
    map((strats) => strats.get(this.modalData.strategy.strategyId)),
  );

  strategyPerAsin$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am: AccountMarketplace) => this.spStrategyService.getStrategyGroups(am.accountId, am.marketplace)),
    map((groups) => groups.get(this.modalData.strategyGroup.strategyGroupId!)),
    map((group) => this.strategyService.getStrategyPerAsin(group!)),
  );

  unavailableAsins = toSignal(this.tacosStrategiesService.unavailableAsinsForSponsoredAds$, {
    initialValue: new Set<string>(),
  });

  strategyAsins = toSignal(this.strategy$.pipe(map((s) => s?.asins?.map((a) => a.asin) ?? [])), { requireSync: true });

  availableAsinsToAdd = computed(() => {
    if (!this.catalogAsins()) return [];
    if (this.modalData.strategy.strategyType === StrategyType.PRODUCT) {
      return (
        this.catalogAsins()?.filter(
          (a) => !this.modalData.strategyGroup.asins.includes(a) && !this.unavailableAsins().has(a),
        ) ?? []
      );
    } else {
      return this.modalData.strategyGroup.asins.filter((a) => !this.strategyAsins().includes(a));
    }
  });

  strategyType: StrategyType | undefined;
  catalogAsins = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am: AccountMarketplace) => this.asinService.getCatalog(am.accountId, am.marketplace)),
      map((catalog) =>
        Array.from(catalog.asinOffers.values())
          .map((a) => a.asin)
          .filter((a): a is string => a !== null),
      ),
    ),
  );

  asinSelection = signal<string[]>([]);
  addAsinSelection = signal<string[]>([]);

  saveAddAsinLabel = computed(() =>
    this.translocoService.translate("sp-substrategy.add_n_products", { count: this.addAsinSelection().length }),
  );

  deleteAsinLoading = signal(false);
  addAsinLoading = signal(false);

  constructor() {
    this.strategyType = this.modalData.strategy.strategyType;
  }

  deleteProductFromStrategy(asins: string[]) {
    if (this.strategyType === StrategyType.PRODUCT) {
      this.deleteProductFromProductStrategy(asins);
    } else {
      this.deleteAsinLoading.set(true);
      this.strategyService.deleteAsinsFromStrategy(this.modalData.strategy, asins).subscribe({
        next: (strategy: Strategy) => {
          this.toastService.success(this.translocoService.translate("common.n_deleted_asins", { count: asins.length }));
          this.asinSelection.set([]);
          this.deleteAsinLoading.set(false);
        },
        error: (error) => {
          this.toastService.error(
            this.translocoService.translate("sp-strategy-group-page.error_removing_products_from_strategy"),
            error,
          );
          this.deleteAsinLoading.set(false);
        },
      });
    }
  }

  private deleteProductFromProductStrategy(asins: string[]) {
    this.strategyPerAsin$
      .pipe(
        take(1),
        map((strategyPerAsin) => this.strategyService.getImpactedStrategiesOnDeleteProduct(asins, strategyPerAsin)),
        switchMap((impactedStrats) => {
          return this.modalService
            .openModal<ConfirmProductsDeletionModalData, void>(ConfirmProductsDeletionModalComponent, {
              modalTitle: this.translocoService.translate("sp-strategy-group-page.product_deletion"),
              data: {
                productsToDelete: asins,
                impactedStrats,
              },
            })
            .afterClosed()
            .pipe(map(() => impactedStrats));
        }),
        tap(() => this.deleteAsinLoading.set(true)),
        switchMap((impactedStrats) =>
          forkJoin(
            Array.from(impactedStrats.keys()).map((strat) =>
              this.strategyService.deleteAsinsFromStrategy(strat, asins),
            ),
          ),
        ),
        // TODO: also stop strategies if no more ASINs
      )
      .subscribe({
        next: () => {
          this.toastService.success(this.translocoService.translate("common.n_deleted_asins", { count: asins.length }));
          this.deleteAsinLoading.set(false);
          this.asinSelection.set([]);
          this.modalRef.close();
        },
        error: (error) => {
          this.toastService.error(
            this.translocoService.translate("sp-strategy-group-page.error_removing_products_from_strategy"),
            error,
          );
          this.deleteAsinLoading.set(false);
        },
      });
  }

  openAddProductModal() {
    this.asinSelection.set([]);
    this.addProductModalRef = this.modalService.openModal(this.addProductModal, {
      modalTitle: this.translocoService.translate("sp-substrategy.add_products"),
      size: "md",
      height: "fill",
    });
  }

  addProductsToStrategy() {
    this.addAsinLoading.set(true);
    this.strategyService.addAsinsToStrategy(this.modalData.strategy, this.addAsinSelection()).subscribe({
      next: (strategy: Strategy) => {
        this.toastService.success(
          this.translocoService.translate("sp-strategy-group-page.products_added_to_strategy"),
          this.translocoService.translate("sp-strategy-group-page.products_added"),
        );
        this.addAsinSelection.set([]);
        this.addProductModalRef?.close();
      },
      error: (e: string) => {
        this.toastService.error(
          e,
          this.translocoService.translate("sp-strategy-group-page.error_adding_products_to_strategy"),
        );
        this.addAsinLoading.set(false);
      },
    });
  }

  moveProductsMainStrategy(asins: string[]) {
    this.modalService
      .openModal<StrategyGroupMoveProductModalData, TargetProductStrategy>(StrategyGroupMoveProductModalComponent, {
        modalTitle: this.translocoService.translate(
          "strategy-group-move-product-modal.move_products_to_another_main_strategy",
        ),
        data: {
          strategyGroup: this.modalData.strategyGroup,
          source: this.modalData.strategy,
          asinsToMove: asins,
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.asinSelection.set([]);
      });
  }
}
